import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import SiteLayout from "@/components/site/SiteLayout";
import ProtectedRoute from "@/components/site/ProtectedRoute";

import Home from "@/pages/Home";
import Sobre from "@/pages/Sobre";
import Produtos from "@/pages/Produtos";
import Catalogos from "@/pages/Catalogos";
import Clientes from "@/pages/Clientes";
import CodigoDeEtica from "@/pages/CodigoDeEtica";
import Consumiveis from "@/pages/Consumiveis";
import SpareParts from "@/pages/SpareParts";
import Contato from "@/pages/Contato";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";

const SEO = {
  "/": {
    title: "Wellpec | Soluções em Equipamentos Industriais, Consumíveis e Spare Parts",
    desc:
      "Fornecedor industrial especializado em materiais consumíveis, spare parts e soluções para óleo e gás, energia e supply chain.",
  },
  "/sobre": {
    title: "Sobre a Wellpec | Fornecedor industrial para óleo, gás e energia",
    desc:
      "Conheça a Wellpec: especialistas no fornecimento de materiais consumíveis, spare parts e equipamentos para operações industriais críticas.",
  },
  "/produtos": {
    title: "Produtos e Soluções | Wellpec",
    desc:
      "Busque equipamentos e materiais nos catálogos Wellpec e conheça nossas linhas de consumíveis, spare parts, ferramentas hidráulicas e containers.",
  },
  "/catalogos": {
    title: "Catálogos e Fichas Técnicas | Wellpec",
    desc:
      "Baixe os catálogos Wellpec e Boxwell, além das fichas técnicas de chaves de torque, tensionadores hidráulicos e soluções industriais.",
  },
  "/clientes": {
    title: "Clientes | Wellpec",
    desc:
      "Conheça empresas de óleo, gás e energia atendidas pela Wellpec em fornecimento de equipamentos, consumíveis e spare parts.",
  },
  "/codigo-de-etica": {
    title: "Código de Ética | Wellpec",
    desc:
      "Princípios e diretrizes de conduta da Wellpec com colaboradores, clientes, fornecedores e parceiros comerciais.",
  },
  "/consumiveis": {
    title: "Materiais Consumíveis Industriais | Wellpec",
    desc:
      "Materiais consumíveis para perfuração, completação e operações industriais. Cotação rápida, rastreabilidade e fornecimento recorrente.",
  },
  "/spare-parts": {
    title: "Spare Parts e Peças de Reposição Industriais | Wellpec",
    desc:
      "Spare parts e peças de reposição para BOP, válvulas, bombas e equipamentos críticos. Redução de downtime e confiabilidade técnica.",
  },
  "/contato": {
    title: "Contato | Wellpec",
    desc:
      "Fale com a Wellpec. Atendimento comercial e técnico para cotações, contratos e suporte em operações industriais.",
  },
};

function SEOAndScroll() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    const meta = SEO[location.pathname];
    if (meta) {
      document.title = meta.title;
      let tag = document.querySelector('meta[name="description"]');
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", "description");
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", meta.desc);

      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", window.location.origin + location.pathname);
    }
  }, [location.pathname]);
  return null;
}

function PublicShell({ children }) {
  return <SiteLayout>{children}</SiteLayout>;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SEOAndScroll />
        <Routes>
          <Route path="/" element={<PublicShell><Home /></PublicShell>} />
          <Route path="/sobre" element={<PublicShell><Sobre /></PublicShell>} />
          <Route path="/produtos" element={<PublicShell><Produtos /></PublicShell>} />
          <Route path="/catalogos" element={<PublicShell><Catalogos /></PublicShell>} />
          <Route path="/clientes" element={<PublicShell><Clientes /></PublicShell>} />
          <Route path="/codigo-de-etica" element={<PublicShell><CodigoDeEtica /></PublicShell>} />
          <Route path="/consumiveis" element={<PublicShell><Consumiveis /></PublicShell>} />
          <Route path="/spare-parts" element={<PublicShell><SpareParts /></PublicShell>} />
          <Route path="/contato" element={<PublicShell><Contato /></PublicShell>} />

          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="*"
            element={
              <PublicShell>
                <div className="max-w-3xl mx-auto px-6 py-32 text-center">
                  <div className="text-xs font-bold uppercase tracking-[0.22em] text-bronze">
                    Erro 404
                  </div>
                  <h1 className="mt-3 font-display font-black text-4xl text-ink">
                    Página não encontrada
                  </h1>
                  <p className="mt-4 text-ink-muted">
                    O endereço que você tentou acessar não existe ou foi movido.
                  </p>
                </div>
              </PublicShell>
            }
          />
        </Routes>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
